import { Record, List, Set } from "immutable";
import { AnyAction } from "redux";

import { defaultValues } from "@config/defaultValues";
import { IS_BROWSER } from "@config/consts";
import { FeatureTogglesType } from "@config/featureToggles";

import { PROMO_CODE_NOTIFICATION, PAYMENT_METHODS } from "@typings/globals";
import { EXTRA_SERVICES_BOOKING, PetsDetails } from "@typings/booking";
import { LOADING_STATE } from "@typings/reduxThunkActions";

import {
  ACTION_SUCCESS,
  ACTION_NO_DATA,
  ACTION_FAIL,
  ACTION_PENDING,
} from "@services/AsyncActionCreatorFactory";
import { getQueryValue } from "@services/QueryParamsParser";

import {
  BookingReducer,
  BookingPricing,
  BookingRegistrationForm,
} from "./booking.typings";
import { BOOKING_ACTIONS } from "./booking.actions";
import {
  bookingTasks,
  pricingFactory,
  pricingInitialState,
  agentsFactory,
  registerFormInitialState,
  bookingFromJobTemplateFactory,
  pricePlansFactory,
  pricePlanFactory,
  cleaningProductsFactory,
  selectedCleaningProductsIdsFactory,
  serviceVoucherPlanFactory,
  frequenciesFactory,
  bookingAvailabilitiesCalendarInitialState,
  bookingAvailabilitiesFactory,
  getNewExecutionDateTime,
} from "./booking.factories";

const getServiceLocationIdFromUrl = (url: string) =>
  !isNaN(Number(getQueryValue(url, "serviceLocationId")))
    ? Number(getQueryValue(url, "serviceLocationId"))
    : defaultValues.booking.serviceLocationId;

export const initialState = Record<BookingReducer>({
  serviceLocationId: IS_BROWSER
    ? getServiceLocationIdFromUrl(window.location.href)
    : defaultValues.booking.serviceLocationId,
  tasks: bookingTasks,
  calendar: Record(bookingAvailabilitiesCalendarInitialState)(),
  pricing: Record<BookingPricing>(pricingInitialState)(),
  pricingLoadingState: defaultValues.booking.pricingLoadingState,
  priceWillChange: defaultValues.booking.priceWillChange,
  pricePlans: List(),
  pricePlansLoadingState: LOADING_STATE.IDLE,
  serviceVoucherPlans: List(),
  serviceVouchersLoadingState: LOADING_STATE.IDLE,
  frequencies: defaultValues.booking.frequencies,
  frequency: defaultValues.booking.cleaningFrequency,
  frequenciesLoadingState: LOADING_STATE.IDLE,
  preselectedAgentUuid: defaultValues.booking.preselectedAgentUuid,
  hasPets: defaultValues.booking.pets.selectedOptionValue,
  petsDetails: Record({ types: defaultValues.booking.pets.types })(),
  agentPreference: defaultValues.booking.agentPreference,
  isChoosingAnyBatmaid: defaultValues.booking.isChoosingAnyBatmaid,
  execution: {
    date: null,
    time: null,
  },
  reservationUuid: defaultValues.booking.reservationUuid,
  isCleaningChecklistAvailable:
    defaultValues.booking.isCleaningChecklistAvailable,
  previousAgents: defaultValues.booking.previousAgents,
  previousAgentsLoadingState: defaultValues.booking.previousAgentsLoadingState,
  voucherToken: defaultValues.booking.voucherToken,
  voucherMessage: defaultValues.booking.voucherMessage,
  voucherStatus: defaultValues.booking.voucherStatus,
  specialInstructions: defaultValues.booking.specialInstructions,
  keyPick: defaultValues.booking.keyPick,
  locationComment: defaultValues.booking.locationComment,
  locationUuid: defaultValues.booking.locationUuid,
  form: Record<BookingRegistrationForm>(registerFormInitialState)(),
  mainLocationUuid: defaultValues.booking.mainLocationUuid,
  availablePaymentMethods: List(),
  selectedPaymentMethod: defaultValues.booking.selectedPaymentMethod,
  paymentMethodsLoadingState: LOADING_STATE.IDLE,
  templateJobId: defaultValues.booking.templateJobId,
  queryParams: defaultValues.booking.queryParams,
  pricePlanUuid: defaultValues.booking.pricePlan,
  cleaningProducts: null,
  selectedCleaningProductsIds: Set(),
  nonStandardFrequencyShifts: defaultValues.booking.nonStandardFrequencyShifts,
  contactAfterBooking: defaultValues.booking.contactAfterBooking,
  hasServiceVouchers: defaultValues.booking.hasServiceVouchers,
  hasUrssaf: defaultValues.booking.hasUrssaf,
  selectedTimeSlotId: null,
});

const bookingReducer = (
  store: Record<BookingReducer> = initialState(),
  action: AnyAction,
): Record<BookingReducer> => {
  const { payload, type, params } = action;
  const featureToggles: FeatureTogglesType = params?.featureToggles ?? {};

  switch (type) {
    case BOOKING_ACTIONS.SET_FROM_LOCAL_STORAGE: {
      return action.payload || store;
    }
    case BOOKING_ACTIONS.LEAVE_PROCESS: {
      return initialState();
    }
    case `${BOOKING_ACTIONS.FETCH_AVAILABILITIES}${ACTION_PENDING}`: {
      return store.setIn(["calendar", "state"], LOADING_STATE.LOADING);
    }
    case `${BOOKING_ACTIONS.FETCH_AVAILABILITIES}${ACTION_FAIL}`: {
      return store.setIn(["calendar", "state"], LOADING_STATE.FAILED);
    }
    case `${BOOKING_ACTIONS.FETCH_AVAILABILITIES}${ACTION_SUCCESS}`: {
      const calendar = bookingAvailabilitiesFactory(
        payload.data,
        !!params?.startDate,
      )
        .set("state", LOADING_STATE.SUCCEEDED)
        .set("isTouched", store.get("calendar").get("isTouched"));
      const defaultSelection = calendar.get("defaultSelection");
      const newExecutionDateTime = getNewExecutionDateTime(
        calendar,
        store.get("execution"),
        defaultSelection,
        params?.isTouched,
      );

      return store
        .set("calendar", calendar)
        .set("execution", newExecutionDateTime);
    }
    case BOOKING_ACTIONS.SET_TOUCH_CALENDAR: {
      return store.setIn(["calendar", "isTouched"], payload);
    }
    case BOOKING_ACTIONS.SET_TASK: {
      return store.mergeIn(["tasks", payload.service], {
        ...payload,
      });
    }
    case BOOKING_ACTIONS.RESET_TASKS: {
      return store.set("tasks", bookingTasks);
    }
    case `${BOOKING_ACTIONS.FETCH_PRICINGS}${ACTION_PENDING}`: {
      return store
        .set("pricingLoadingState", LOADING_STATE.LOADING)
        .set("priceWillChange", true);
    }
    case `${BOOKING_ACTIONS.FETCH_PRICINGS}${ACTION_SUCCESS}`:
    case `${BOOKING_ACTIONS.FETCH_URSSAF_PRICINGS}${ACTION_SUCCESS}`: {
      return store
        .set("pricing", pricingFactory(payload.data))
        .set("pricingLoadingState", LOADING_STATE.SUCCEEDED)
        .set("priceWillChange", false);
    }
    case `${BOOKING_ACTIONS.FETCH_PRICINGS}${ACTION_FAIL}`: {
      return store
        .set("pricingLoadingState", LOADING_STATE.FAILED)
        .set("priceWillChange", false);
    }
    case `${BOOKING_ACTIONS.FETCH_PLANS}${ACTION_PENDING}`: {
      return store.set("pricePlansLoadingState", LOADING_STATE.LOADING);
    }
    case `${BOOKING_ACTIONS.FETCH_PLANS}${ACTION_SUCCESS}`: {
      const plans = pricePlansFactory(payload.data);
      const newPricePlanUuid = pricePlanFactory(store.get("frequency"), plans);
      const newPricePlan = plans.find(
        plan => plan.get("uuid") === newPricePlanUuid,
      );

      return store
        .set("pricePlansLoadingState", LOADING_STATE.SUCCEEDED)
        .set("pricePlans", plans)
        .set(
          "selectedCleaningProductsIds",
          selectedCleaningProductsIdsFactory(
            newPricePlan,
            store.get("cleaningProducts"),
            store.get("selectedCleaningProductsIds"),
          ),
        );
    }
    case `${BOOKING_ACTIONS.FETCH_PLANS}${ACTION_FAIL}`: {
      return store.set("pricePlansLoadingState", LOADING_STATE.FAILED);
    }
    case BOOKING_ACTIONS.SET_FREQUENCY: {
      const newServicePlanUuid =
        store
          .get("serviceVoucherPlans")
          .find(plan => plan.get("frequency") === payload.frequency)
          ?.get("uuid") ?? null;

      const newPricePlanUuid = pricePlanFactory(
        payload.frequency,
        store.get("pricePlans"),
        payload.serviceLocationId,
      );

      const newPricePlan = store
        .get("pricePlans")
        .find(plan => plan.get("uuid") === newPricePlanUuid);

      return store
        .set("frequency", payload.frequency)
        .set(
          "pricePlanUuid",
          featureToggles.service_voucher
            ? newServicePlanUuid
            : newPricePlanUuid,
        )
        .set(
          "selectedCleaningProductsIds",
          selectedCleaningProductsIdsFactory(
            newPricePlan,
            store.get("cleaningProducts"),
            store.get("selectedCleaningProductsIds"),
          ),
        );
    }
    case BOOKING_ACTIONS.SET_AGENT_PREFERENCE: {
      return store.set("agentPreference", payload.agentPreference);
    }
    case BOOKING_ACTIONS.SET_PRESELECTED_AGENT_UUID: {
      const hasServiceVouchers = store.get("hasServiceVouchers");
      const selectedAgentUuid = payload.preselectedAgentUuid;
      const selectedAgent = store
        .get("previousAgents")
        .find(item => item.get("uuid") === selectedAgentUuid);
      const ironingTask = store.getIn([
        "tasks",
        EXTRA_SERVICES_BOOKING.IRONING,
      ]);

      // deselecting ironing task for an agent without the ironing skill
      if (
        hasServiceVouchers &&
        selectedAgent &&
        !selectedAgent?.get("doesIroning") &&
        ironingTask.get("selected")
      ) {
        return store
          .set("preselectedAgentUuid", selectedAgentUuid)
          .mergeIn(["tasks", EXTRA_SERVICES_BOOKING.IRONING], {
            ...ironingTask,
            selected: false,
          });
      }

      return store.set("preselectedAgentUuid", selectedAgentUuid);
    }
    case BOOKING_ACTIONS.SET_EXECUTION: {
      return store.set("execution", {
        ...store.get("execution"),
        ...payload,
      });
    }
    case `${BOOKING_ACTIONS.MAKE_RESERVATION}${ACTION_SUCCESS}`: {
      return store
        .set("reservationUuid", payload.data.reservationUuid)
        .set(
          "isCleaningChecklistAvailable",
          payload.data.isCleaningChecklistAvailable ??
            defaultValues.booking.isCleaningChecklistAvailable,
        );
    }
    case `${BOOKING_ACTIONS.FETCH_PREVIOUS_AGENTS}${ACTION_PENDING}`: {
      return store.set("previousAgentsLoadingState", LOADING_STATE.LOADING);
    }
    case `${BOOKING_ACTIONS.FETCH_PREVIOUS_AGENTS}${ACTION_SUCCESS}`: {
      return store
        .set("previousAgents", agentsFactory(payload.data))
        .set("previousAgentsLoadingState", LOADING_STATE.SUCCEEDED);
    }
    case `${BOOKING_ACTIONS.FETCH_PREVIOUS_AGENTS}${ACTION_FAIL}`: {
      return store.set("previousAgentsLoadingState", LOADING_STATE.FAILED);
    }
    case `${BOOKING_ACTIONS.SET_SERVICE_LOCATION}`: {
      return store.set("serviceLocationId", payload);
    }
    case `${BOOKING_ACTIONS.FETCH_VOUCHER}${ACTION_SUCCESS}`: {
      return store
        .set("voucherToken", params.token)
        .set("voucherMessage", payload.data.translatedSuccessMessage)
        .set("voucherStatus", PROMO_CODE_NOTIFICATION.SUCCESS);
    }
    case `${BOOKING_ACTIONS.FETCH_VOUCHER}${ACTION_FAIL}`: {
      return store
        .set("voucherToken", defaultValues.booking.voucherToken)
        .set("voucherMessage", action.translatedErrorMessage)
        .set("voucherStatus", PROMO_CODE_NOTIFICATION.FAILURE);
    }
    case BOOKING_ACTIONS.RESET_VOUCHER: {
      return store
        .set("voucherToken", defaultValues.booking.voucherToken)
        .set("voucherMessage", defaultValues.booking.voucherMessage)
        .set("voucherStatus", defaultValues.booking.voucherStatus);
    }
    case BOOKING_ACTIONS.SET_VOUCHER: {
      return store
        .set("voucherMessage", payload.voucherMessage)
        .set("voucherStatus", payload.voucherStatus);
    }
    case BOOKING_ACTIONS.SET_SPECIAL_INSTRUCTIONS: {
      return store.set("specialInstructions", payload);
    }
    case BOOKING_ACTIONS.SET_KEY_PICK: {
      return store.set("keyPick", payload);
    }
    case BOOKING_ACTIONS.SET_LOCATION_COMMENT: {
      return store.set("locationComment", payload);
    }
    case BOOKING_ACTIONS.SET_LOCATION_UUID: {
      return store.set("locationUuid", payload);
    }
    case `${BOOKING_ACTIONS.ADD_LOCATION}${ACTION_SUCCESS}`: {
      return store.set("locationUuid", payload.data.uuid);
    }
    case BOOKING_ACTIONS.SET_PETS: {
      const { checked, ...petsDetails } = payload;

      return store
        .set("hasPets", checked)
        .set("petsDetails", Record(petsDetails as PetsDetails)());
    }
    case BOOKING_ACTIONS.SET_REGISTRATION_FORM: {
      return store.mergeIn(["form"], payload);
    }
    case `${BOOKING_ACTIONS.REGISTER_CLIENT}${ACTION_SUCCESS}`: {
      return store
        .set("locationUuid", payload.data.mainLocationUuid)
        .set("mainLocationUuid", payload.data.mainLocationUuid);
    }
    case `${BOOKING_ACTIONS.CANCEL_RESERVATION}${ACTION_NO_DATA}`:
    case `${BOOKING_ACTIONS.CANCEL_RESERVATION}${ACTION_FAIL}`: {
      return store
        .set("reservationUuid", defaultValues.booking.reservationUuid)
        .set(
          "isCleaningChecklistAvailable",
          defaultValues.booking.isCleaningChecklistAvailable,
        );
    }
    case `${BOOKING_ACTIONS.FETCH_PAYMENT_METHODS}${ACTION_PENDING}`: {
      return store.set("paymentMethodsLoadingState", LOADING_STATE.LOADING);
    }
    case `${BOOKING_ACTIONS.FETCH_PAYMENT_METHODS}${ACTION_SUCCESS}`: {
      return store
        .set("availablePaymentMethods", List<PAYMENT_METHODS>(payload.data))
        .set("paymentMethodsLoadingState", LOADING_STATE.SUCCEEDED);
    }
    case `${BOOKING_ACTIONS.FETCH_PAYMENT_METHODS}${ACTION_FAIL}`: {
      return store.set("paymentMethodsLoadingState", LOADING_STATE.FAILED);
    }
    case BOOKING_ACTIONS.SET_PAYMENT_METHOD: {
      return store.set("selectedPaymentMethod", payload);
    }
    case BOOKING_ACTIONS.SET_TEMPLATE_JOB_ID: {
      return store.set("templateJobId", payload);
    }
    case BOOKING_ACTIONS.SET_FROM_JOB_TEMPLATE: {
      const bookingFromJobTemplate = bookingFromJobTemplateFactory(
        payload,
        bookingTasks,
      );
      const mergedStore = store.merge(bookingFromJobTemplate);

      return mergedStore;
    }
    case BOOKING_ACTIONS.SET_QUERY_PARAMS: {
      return store.set("queryParams", payload);
    }
    case BOOKING_ACTIONS.SET_IS_CHOOSING_ANY_BATMAID: {
      return store.set("isChoosingAnyBatmaid", payload);
    }
    case BOOKING_ACTIONS.SET_PRICE_PLAN: {
      const newPricePlan = store
        .get("pricePlans")
        .find(plan => plan.get("uuid") === payload.pricePlanUuid);

      return store
        .set("pricePlanUuid", payload.pricePlanUuid)
        .set(
          "selectedCleaningProductsIds",
          selectedCleaningProductsIdsFactory(
            newPricePlan,
            store.get("cleaningProducts"),
            store.get("selectedCleaningProductsIds"),
          ),
        );
    }
    case `${BOOKING_ACTIONS.FETCH_CLEANING_PRODUCTS}${ACTION_FAIL}`: {
      const products = cleaningProductsFactory([]);

      return store.set("cleaningProducts", products);
    }
    case `${BOOKING_ACTIONS.FETCH_CLEANING_PRODUCTS}${ACTION_SUCCESS}`: {
      const products = cleaningProductsFactory(payload.data);
      const pricePlanUuid = store.get("pricePlanUuid");
      const pricePlan = store
        .get("pricePlans")
        .find(plan => plan.get("uuid") === pricePlanUuid);

      return store
        .set("cleaningProducts", products)
        .set(
          "selectedCleaningProductsIds",
          selectedCleaningProductsIdsFactory(
            pricePlan,
            products,
            store.get("selectedCleaningProductsIds"),
          ),
        );
    }
    case BOOKING_ACTIONS.ADD_CLEANING_PRODUCT_ID: {
      return store.set(
        "selectedCleaningProductsIds",
        store.get("selectedCleaningProductsIds").add(payload),
      );
    }
    case BOOKING_ACTIONS.REMOVE_CLEANING_PRODUCT_ID: {
      return store.set(
        "selectedCleaningProductsIds",
        store.get("selectedCleaningProductsIds").delete(payload),
      );
    }
    case BOOKING_ACTIONS.SET_NON_STANDARD_FREQUENCY_CLEANINGS: {
      return store.set("nonStandardFrequencyShifts", payload);
    }
    case BOOKING_ACTIONS.SET_CONTACT_AFTER_BOOKING: {
      return store.set("contactAfterBooking", payload);
    }
    case BOOKING_ACTIONS.SET_HAS_SERVICE_VOUCHERS: {
      const currentTask = store.getIn([
        "tasks",
        EXTRA_SERVICES_BOOKING.HOME_CLEANING,
      ]);

      return store
        .set("hasServiceVouchers", payload)
        .mergeIn(["tasks", EXTRA_SERVICES_BOOKING.HOME_CLEANING], {
          ...payload,
          ...(payload && { duration: Math.ceil(currentTask.duration) }),
        });
    }
    case `${BOOKING_ACTIONS.FETCH_SERVICE_VOUCHER_PLANS}${ACTION_PENDING}`: {
      return store.set("serviceVouchersLoadingState", LOADING_STATE.LOADING);
    }
    case `${BOOKING_ACTIONS.FETCH_SERVICE_VOUCHER_PLANS}${ACTION_SUCCESS}`: {
      const serviceVoucherPlans = serviceVoucherPlanFactory(payload.data);
      const mostPopularPlanFromDefaultValues = serviceVoucherPlans.find(
        plan =>
          plan.get("frequency") === defaultValues.booking.cleaningFrequency,
      );

      const selectedPlan =
        mostPopularPlanFromDefaultValues?.get("uuid") ||
        serviceVoucherPlans.get(0)?.get("uuid") ||
        null;

      return store
        .set("serviceVoucherPlans", serviceVoucherPlans)
        .set("pricePlanUuid", selectedPlan)
        .set("serviceVouchersLoadingState", LOADING_STATE.SUCCEEDED);
    }
    case `${BOOKING_ACTIONS.FETCH_SERVICE_VOUCHER_PLANS}${ACTION_FAIL}`: {
      return store.set("serviceVouchersLoadingState", LOADING_STATE.FAILED);
    }
    case `${BOOKING_ACTIONS.SET_HAS_URSSAF}`: {
      return store.set("hasUrssaf", payload);
    }
    case BOOKING_ACTIONS.SET_TIME_SLOT_ID: {
      return store.set("selectedTimeSlotId", payload);
    }
    case `${BOOKING_ACTIONS.FETCH_FREQUENCIES}${ACTION_PENDING}`: {
      return store.set("frequenciesLoadingState", LOADING_STATE.LOADING);
    }
    case `${BOOKING_ACTIONS.FETCH_FREQUENCIES}${ACTION_SUCCESS}`: {
      return store
        .set("frequencies", frequenciesFactory(payload.data))
        .set("frequency", payload.data.default)
        .set("frequenciesLoadingState", LOADING_STATE.SUCCEEDED);
    }
    case `${BOOKING_ACTIONS.FETCH_FREQUENCIES}${ACTION_FAIL}`: {
      return store
        .set("frequencies", List(defaultValues.booking.frequencies))
        .set("frequenciesLoadingState", LOADING_STATE.FAILED);
    }
    case BOOKING_ACTIONS.SET_PRICE_WILL_CHANGE: {
      return store.set("priceWillChange", payload);
    }
    default: {
      return store;
    }
  }
};

export { bookingReducer };
